import React, { useEffect, useState } from 'react';
import { BarChart3, TrendingUp, TrendingDown, Calendar } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { BudgetingService } from '../../lib/budgetingService';


export function YearlySummary() {
  const { user } = useAuth();
  const [yearlyData, setYearlyData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

  useEffect(() => {
    loadYearlySummary();
  }, [user?.id, currentYear]);

  const loadYearlySummary = async () => {
    if (!user?.id) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      // In a real app, you'd fetch aggregated yearly data
      // For now, we'll simulate some data or use a simplified aggregation
      const allTransactions = await BudgetingService.getBudgetTransactions(user.id);

      const monthlyAggregates = Array.from({ length: 12 }, (_, i) => {
        const month = i + 1;
        const monthTransactions = allTransactions.filter(t =>
          new Date(t.transaction_date).getFullYear() === currentYear &&
          new Date(t.transaction_date).getMonth() + 1 === month
        );

        const income = monthTransactions.filter(t => t.type === 'income').reduce((sum, t) => sum + Number(t.amount), 0);
        const expenses = monthTransactions.filter(t => t.type === 'expense').reduce((sum, t) => sum + Number(t.amount), 0);

        return {
          month: new Date(currentYear, i, 1).toLocaleString('en-AU', { month: 'short' }),
          income,
          expenses,
        };
      });
      setYearlyData(monthlyAggregates);

    } catch (error) {
      console.error('Error loading yearly summary:', error);
      setYearlyData([]);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="h-7 bg-gray-200 rounded w-64 mb-6 animate-pulse" />
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-8 animate-pulse">
          <div className="h-5 bg-gray-200 rounded w-48 mb-4" />
          <div className="h-80 flex items-end justify-around space-x-2">
            {Array.from({ length: 12 }, (_, i) => (
              <div key={i} className="flex flex-col items-center h-full justify-end">
                <div className="w-8 bg-gray-200 rounded-t-md" style={{ height: `${20 + Math.random() * 60}%` }} />
                <div className="h-3 w-6 bg-gray-200 rounded mt-2" />
              </div>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[1, 2].map(i => (
            <div key={i} className="bg-white p-6 rounded-lg shadow-sm border animate-pulse">
              <div className="h-5 bg-gray-200 rounded w-40 mb-4" />
              <div className="h-8 bg-gray-200 rounded w-32" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Yearly Financial Summary - {currentYear}</h2>

      {/* Placeholder for yearly bar chart */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Income vs Expenses by Month</h3>
        {yearlyData.length > 0 ? (
          <div className="h-80 flex items-end justify-around space-x-2">
            {yearlyData.map((data, index) => (
              <div key={index} className="flex flex-col items-center h-full justify-end">
                <div className="relative flex items-end h-full w-8">
                  <div
                    className="absolute bottom-0 w-full bg-green-500 rounded-t-md"
                    style={{ height: `${(data.income / Math.max(data.income, data.expenses, 1)) * 90}%` }}
                    title={`Income: $${data.income.toFixed(2)}`}
                  />
                  <div
                    className="absolute bottom-0 w-full bg-red-500 rounded-t-md"
                    style={{ height: `${(data.expenses / Math.max(data.income, data.expenses, 1)) * 90}%`, zIndex: 1 }}
                    title={`Expenses: $${data.expenses.toFixed(2)}`}
                  />
                </div>
                <span className="text-xs text-gray-600 mt-2">{data.month}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            <BarChart3 className="w-16 h-16 mx-auto mb-4" />
            <p>No yearly data to display.</p>
            <p className="text-sm text-gray-400">Add some transactions to see your yearly trends.</p>
          </div>
        )}
      </div>

      {/* Placeholder for other yearly metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Total Income (Year)</h3>
          <p className="text-3xl font-bold text-green-600">
            {new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(
              yearlyData.reduce((sum, d) => sum + d.income, 0)
            )}
          </p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Total Expenses (Year)</h3>
          <p className="text-3xl font-bold text-red-600">
            {new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(
              yearlyData.reduce((sum, d) => sum + d.expenses, 0)
            )}
          </p>
        </div>
      </div>
    </div>
  );
}


